import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  SimpleGrid,
  Badge,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Card,
  useColorModeValue,
  useColorMode,
  useToast,
  IconButton,
  Collapse,
} from '@chakra-ui/react';
import {
  MdQrCodeScanner,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdSearch,
  MdRefresh,
  MdPeople,
  MdCameraAlt,
  MdFlipCameraAndroid,
  MdHighlight,
  MdStop,
  MdLogout,
  MdHistory,
  MdKeyboard,
  MdClose,
  MdDarkMode,
  MdLightMode,
} from 'react-icons/md';
import axios from 'axios';
import { useAuthStore, signOut } from 'store/auth.store';
import { Html5Qrcode } from 'html5-qrcode';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Audio feedback synthesizer using Web Audio API (no external audio files needed)
const playBeep = (type) => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'success') {
      // High-pitched affirmative chime (880Hz -> 1320Hz)
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.28);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } else if (type === 'duplicate') {
      // Lower double-pulse buzz (warning)
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(400, ctx.currentTime);
      osc.frequency.setValueAtTime(240, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.38);
    } else {
      // Sharp error buzz
      osc.type = 'square';
      osc.frequency.setValueAtTime(180, ctx.currentTime);
      gain.gain.setValueAtTime(0.45, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.38);
    }
  } catch (e) {
    // Audio context may be restricted by browser policy before first interaction
  }

  // Mobile vibration feedback
  try {
    if (navigator.vibrate) {
      if (type === 'success') {
        navigator.vibrate(90);
      } else if (type === 'duplicate') {
        navigator.vibrate([120, 60, 120]);
      } else {
        navigator.vibrate(250);
      }
    }
  } catch (e) { }
};

export default function CheckInDashboardView({ isMobileAppView = false }) {
  const { token, user } = useAuthStore();
  const [forceGatekeeperMode, setForceGatekeeperMode] = useState(false);
  const isGateKeeper = user?.role === 'gate_keeper' || isMobileAppView || forceGatekeeperMode;

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  };

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(() => {
    try {
      const saved = localStorage.getItem('bamyan_gatekeeper_selected_event_id');
      return saved ? Number(saved) : '';
    } catch (e) {
      return '';
    }
  });
  const [scanCode, setScanCode] = useState('');
  const [gateName, setGateName] = useState('Main Entrance');
  const [scanDirection, setScanDirection] = useState('in'); // 'in' (Entry) | 'out' (Exit)
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Dynamic gates derived from the selected event's venue
  const selectedEvent = events.find((e) => Number(e.id) === Number(selectedEventId));
  const venueGates = React.useMemo(() => {
    const g = selectedEvent?.venue?.gates;
    if (Array.isArray(g) && g.length > 0) {
      return g.map((item) => (typeof item === 'object' && item?.name ? item.name : String(item)));
    }
    return ['Main Entrance'];
  }, [selectedEvent]);

  // Feed Filter States
  const [feedStatusFilter, setFeedStatusFilter] = useState('all');
  const [feedGateFilter, setFeedGateFilter] = useState('all');
  const [feedSeatFilter, setFeedSeatFilter] = useState('');
  const [feedEventFilter, setFeedEventFilter] = useState('');

  // Sanitize message to never expose raw 64-char hashes
  const formatScanMessage = (rawMsg) => {
    if (!rawMsg) return '';
    let text = String(rawMsg);
    text = text.replace(/"?[a-f0-9]{24,64}"?/gi, 'ticket');
    return text;
  };

  // Filtered recent logs for live audit feed
  const filteredRecentLogs = React.useMemo(() => {
    return recentLogs.filter((log) => {
      // Event filter
      if (feedEventFilter && String(log.event_id) !== String(feedEventFilter)) {
        return false;
      }
      // Status filter
      if (feedStatusFilter !== 'all') {
        const s = String(log.status || log.result_code || '').toLowerCase();
        if (feedStatusFilter === 'valid' && s !== 'valid' && s !== 'success') return false;
        if (feedStatusFilter === 'exit' && s !== 'exit' && s !== 'exit_granted') return false;
        if (feedStatusFilter === 'duplicate' && s !== 'duplicate') return false;
        if (feedStatusFilter === 'invalid' && s !== 'invalid' && s !== 'wrong_gate' && s !== 'wrong_event') return false;
      }
      // Gate filter
      if (feedGateFilter !== 'all') {
        if (String(log.gate_name || '').toLowerCase() !== feedGateFilter.toLowerCase()) {
          return false;
        }
      }
      // Seat / Attendee / Code search
      if (feedSeatFilter.trim()) {
        const q = feedSeatFilter.trim().toLowerCase();
        const attendee = String(log.attendee_name || '').toLowerCase();
        const seat = String(log.seat_guid || '').toLowerCase();
        const code = String(log.ticket_code || '').toLowerCase();
        if (!attendee.includes(q) && !seat.includes(q) && !code.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [recentLogs, feedEventFilter, feedStatusFilter, feedGateFilter, feedSeatFilter]);

  // Keep gateName in sync with available venue gates
  useEffect(() => {
    if (venueGates.length > 0 && !venueGates.includes(gateName)) {
      setGateName(venueGates[0]);
    }
  }, [venueGates, gateName]);

  // Camera State
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('environment'); // 'environment' (back) or 'user' (front)
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [showRecentDrawer, setShowRecentDrawer] = useState(false);

  const html5QrCodeRef = useRef(null);
  const lastScannedTimeRef = useRef(0);
  const lastScannedCodeRef = useRef('');
  const lastCodeScannedTimeRef = useRef(0);
  const isScanningRef = useRef(false);
  const scannerBufferRef = useRef('');
  const lastKeystrokeTimeRef = useRef(0);
  const toast = useToast();

  // Mutable refs to prevent stale closure when camera stream is running
  const selectedEventIdRef = useRef(selectedEventId);
  const gateNameRef = useRef(gateName);
  const scanDirectionRef = useRef(scanDirection);
  const handleCameraDecodedRef = useRef(null);

  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
  }, [selectedEventId]);

  useEffect(() => {
    gateNameRef.current = gateName;
  }, [gateName]);

  useEffect(() => {
    scanDirectionRef.current = scanDirection;
  }, [scanDirection]);

  // Helper to parse dates safely across all browsers (including iOS Safari)
  const parseEventDate = (dateStr) => {
    if (!dateStr) return null;
    const safeStr = typeof dateStr === 'string' ? dateStr.trim().replace(' ', 'T') : dateStr;
    const d = new Date(safeStr);
    return isNaN(d.getTime()) ? null : d;
  };

  // Check if an event's date has passed
  const isPastEvent = (evt) => {
    if (evt.status === 'cancelled' || evt.status === 'archived') return true;

    const now = Date.now();
    // If end_datetime is present, allow a 10-hour grace period after it finishes (for late night check-ins)
    if (evt.end_datetime) {
      const end = parseEventDate(evt.end_datetime);
      if (end) {
        return end.getTime() + (10 * 60 * 60 * 1000) < now;
      }
    }

    // If only start_datetime is present, allow a 14-hour grace period after start time
    if (evt.start_datetime) {
      const start = parseEventDate(evt.start_datetime);
      if (start) {
        return start.getTime() + (14 * 60 * 60 * 1000) < now;
      }
    }

    return false;
  };

  // User event selector change handler (persists to localStorage and clears debounce)
  const handleEventChange = (newVal) => {
    const nextId = newVal ? Number(newVal) : '';
    setSelectedEventId(nextId);
    selectedEventIdRef.current = nextId;

    if (nextId) {
      try {
        localStorage.setItem('bamyan_gatekeeper_selected_event_id', String(nextId));
      } catch (e) {}
    } else {
      try {
        localStorage.removeItem('bamyan_gatekeeper_selected_event_id');
      } catch (e) {}
    }

    // Reset camera deduplication refs immediately so the new event can scan tickets right away
    lastScannedCodeRef.current = '';
    lastCodeScannedTimeRef.current = 0;
    setLastResult(null);

    // Immediately fetch stats for the newly chosen event
    if (nextId) {
      fetchStats(nextId);
    }
  };

  const updateGateName = (name) => {
    setGateName(name);
    gateNameRef.current = name;
  };

  const updateScanDirection = (dir) => {
    setScanDirection(dir);
    scanDirectionRef.current = dir;
    lastScannedCodeRef.current = '';
    lastCodeScannedTimeRef.current = 0;
  };

  const { colorMode, toggleColorMode } = useColorMode();
  const cardBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const gkBg = useColorModeValue('gray.50', '#0a0f1d');
  const gkHeaderBg = useColorModeValue('white', '#111827');
  const gkBorderColor = useColorModeValue('gray.200', 'gray.800');
  const gkInputBg = useColorModeValue('gray.100', '#1f2937');
  const gkInputBorder = useColorModeValue('gray.300', 'gray.700');
  const gkTextColor = useColorModeValue('gray.800', 'white');
  const gkMutedText = useColorModeValue('gray.600', 'gray.400');

  // Load events, filter out past events, and preserve user selection
  useEffect(() => {
    axios
      .get(`${API_BASE}/get_events`, authHeaders)
      .then((res) => {
        const evts = res.data?.data || res.data || [];
        // Filter out past events
        const upcomingAndActive = evts.filter((e) => !isPastEvent(e));
        const finalEvents = upcomingAndActive.length > 0 ? upcomingAndActive : evts;

        // Sort events chronologically (soonest / current first)
        finalEvents.sort((a, b) => {
          const tA = parseEventDate(a.start_datetime)?.getTime() || 0;
          const tB = parseEventDate(b.start_datetime)?.getTime() || 0;
          return tA - tB;
        });

        setEvents(finalEvents);

        // Check if user has an existing saved selection in localStorage
        let savedId = null;
        try {
          const raw = localStorage.getItem('bamyan_gatekeeper_selected_event_id');
          if (raw) savedId = Number(raw);
        } catch (e) {}

        const matchedSaved = savedId ? finalEvents.find((e) => Number(e.id) === savedId) : null;

        if (matchedSaved) {
          // Keep user's chosen event across refresh and logout
          setSelectedEventId(matchedSaved.id);
          selectedEventIdRef.current = matchedSaved.id;
        } else if (finalEvents.length > 0) {
          // If no saved selection or saved event is not in current active events, pick today's or first active
          const today = new Date().toISOString().split('T')[0];
          const todayEvent = finalEvents.find((e) => e.start_datetime && e.start_datetime.startsWith(today));
          const defaultEvent = todayEvent || finalEvents.find((e) => e.status === 'published') || finalEvents[0];
          setSelectedEventId(defaultEvent.id);
          selectedEventIdRef.current = defaultEvent.id;
          try {
            localStorage.setItem('bamyan_gatekeeper_selected_event_id', String(defaultEvent.id));
          } catch (e) {}
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hardware Laser Scanner Gun (HID Keyboard mode) global listener
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Don't hijack keystrokes if the user is typing in a search input
      const target = e.target;
      const isSearchInput =
        target &&
        (target.getAttribute('placeholder')?.toLowerCase().includes('search') ||
          target.getAttribute('name') === 'searchQuery');

      if (isSearchInput) return;

      const now = Date.now();
      const timeDiff = now - lastKeystrokeTimeRef.current;
      lastKeystrokeTimeRef.current = now;

      // Laser scanner bursts are typically < 60ms between keystrokes.
      // If interval exceeds 150ms, it is human manual typing, so reset buffer.
      if (timeDiff > 150) {
        scannerBufferRef.current = '';
      }

      if (e.key === 'Enter') {
        const bufferedCode = scannerBufferRef.current.trim();
        if (bufferedCode.length >= 3) {
          e.preventDefault();
          scannerBufferRef.current = '';
          processTicketCode(bufferedCode);
        }
      } else if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        scannerBufferRef.current += e.key;
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Stats & Recent Logs
  const fetchStats = async (overrideId) => {
    const targetId = overrideId !== undefined ? overrideId : selectedEventIdRef.current;
    if (!targetId) return;
    try {
      const res = await axios.get(`${API_BASE}/check-in/stats/${targetId}`, authHeaders);
      if (res.data?.status) {
        setStats(res.data.data);
        setRecentLogs(res.data.data.recent_logs || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (selectedEventId) {
      fetchStats(selectedEventId);
    }
    const timer = setInterval(() => {
      if (selectedEventIdRef.current) {
        fetchStats(selectedEventIdRef.current);
      }
    }, 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  // Clean up camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Camera Scanner Functions
  const startCamera = async (facing = cameraFacing) => {
    setCameraError(null);
    setIsCameraActive(true);

    try {
      if (html5QrCodeRef.current) {
        await stopCamera();
      }

      // Small tick so DOM is painted with active layout and positive width/height
      await new Promise((r) => setTimeout(r, 80));

      const qrContainer = document.getElementById('qr-reader-viewport');
      if (!qrContainer) {
        throw new Error('Camera viewport element not found in DOM.');
      }

      const qrScanner = new Html5Qrcode('qr-reader-viewport', {
        verbose: false,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true,
        },
      });
      html5QrCodeRef.current = qrScanner;

      const config = {
        fps: 20,
        qrbox: (viewfinderWidth, viewfinderHeight) => {
          const w = viewfinderWidth && viewfinderWidth > 50 ? viewfinderWidth : 300;
          const h = viewfinderHeight && viewfinderHeight > 50 ? viewfinderHeight : 300;
          const minEdge = Math.min(w, h);
          const qrEdgeSize = Math.max(160, Math.floor(minEdge * 0.75));
          return { width: qrEdgeSize, height: qrEdgeSize };
        },
        aspectRatio: 1.0,
      };

      // Determine camera config with fallback
      let cameraConfig = { facingMode: facing };
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras && cameras.length > 0) {
          if (facing === 'environment') {
            const back = cameras.find((c) => /back|rear|environment|back-facing/i.test(c.label)) || cameras[cameras.length - 1];
            if (back) cameraConfig = back.id;
          } else {
            const front = cameras.find((c) => /front|user|selfie/i.test(c.label)) || cameras[0];
            if (front) cameraConfig = front.id;
          }
        }
      } catch (e) {
        cameraConfig = { facingMode: facing };
      }

      try {
        await qrScanner.start(
          cameraConfig,
          config,
          (decodedText) => {
            handleCameraDecodedRef.current?.(decodedText);
          },
          () => {}
        );
      } catch (startErr) {
        console.warn('Initial camera start failed, retrying with simple constraint...', startErr);
        await qrScanner.start(
          { facingMode: 'environment' },
          config,
          (decodedText) => {
            handleCameraDecodedRef.current?.(decodedText);
          },
          () => {}
        );
      }

      // Ensure iOS WebKit plays the video without freeze or black screen
      const videoEl = qrContainer.querySelector('video');
      if (videoEl) {
        videoEl.setAttribute('playsinline', 'true');
        videoEl.setAttribute('webkit-playsinline', 'true');
        videoEl.muted = true;
        videoEl.play().catch(() => {});
      }

      setIsCameraActive(true);

      // Check torch support
      try {
        const track = qrScanner.getRunningTrackCameraCapabilities();
        if (track && track.torchFeature && track.torchFeature().isSupported()) {
          setTorchSupported(true);
        } else {
          setTorchSupported(false);
        }
      } catch (e) {
        setTorchSupported(false);
      }
    } catch (err) {
      console.error('Camera start error:', err);
      setCameraError(
        'Camera access denied or unavailable. Please enable camera permissions in your browser.'
      );
      setIsCameraActive(false);
    }
  };

  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        await html5QrCodeRef.current.clear();
      } catch (e) {
        console.warn('Error stopping camera:', e);
      }
      html5QrCodeRef.current = null;
    }
    setIsCameraActive(false);
    setIsTorchOn(false);
  };

  const toggleCameraFacing = async () => {
    const nextFacing = cameraFacing === 'environment' ? 'user' : 'environment';
    setCameraFacing(nextFacing);
    if (isCameraActive) {
      await startCamera(nextFacing);
    }
  };

  const toggleTorch = async () => {
    if (!html5QrCodeRef.current || !isCameraActive) return;
    try {
      const nextTorch = !isTorchOn;
      await html5QrCodeRef.current.applyVideoConstraints({
        advanced: [{ torch: nextTorch }],
      });
      setIsTorchOn(nextTorch);
    } catch (e) {
      console.warn('Torch toggle failed', e);
    }
  };

  // Debounced scan handler from Camera
  const handleCameraDecoded = (codeText) => {
    const code = String(codeText || '').trim();
    if (!code || isScanningRef.current) {
      return;
    }

    const now = Date.now();
    // If the scanner is held over the exact same ticket, ignore for 8 seconds to prevent accidental duplicate warning
    if (code === lastScannedCodeRef.current && now - lastCodeScannedTimeRef.current < 8000) {
      return;
    }

    // Minimum 1.2s cooldown between any consecutive scans
    if (now - lastScannedTimeRef.current < 1200) {
      return;
    }

    lastScannedTimeRef.current = now;
    lastScannedCodeRef.current = code;
    lastCodeScannedTimeRef.current = now;

    processTicketCode(code);
  };

  handleCameraDecodedRef.current = handleCameraDecoded;

  // Core Ticket Verification
  const processTicketCode = async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code || isScanningRef.current) return;

    isScanningRef.current = true;
    setIsScanning(true);
    try {
      const currentEventId = selectedEventIdRef.current ? Number(selectedEventIdRef.current) : null;
      const currentGateName = gateNameRef.current || 'Main Entrance';
      const currentDirection = scanDirectionRef.current || 'in';

      const res = await axios.post(
        `${API_BASE}/check-in/scan`,
        {
          qr_code_hash: code,
          event_id: currentEventId,
          gate_name: currentGateName,
          direction: currentDirection,
        },
        authHeaders
      );

      const ticketData = res.data.data;
      const isExit = currentDirection === 'out';
      playBeep('success');

      if (!selectedEventIdRef.current && ticketData?.event_id) {
        handleEventChange(ticketData.event_id);
      }

      setLastResult({
        status: isExit ? 'exit' : 'success',
        message: res.data.message || (isExit ? 'Exit Recorded' : 'Ticket Valid - Entry Granted!'),
        data: ticketData,
        timestamp: new Date().toLocaleTimeString(),
      });

      toast({
        title: isExit ? 'Check-Out Registered!' : 'Entry Granted!',
        description: `${ticketData.attendee_name || 'Guest'} • ${ticketData.ticket_tier || ticketData.tier_name || 'General'} • ${ticketData.seat_guid || 'General'}${ticketData.event_title ? ' • ' + ticketData.event_title : ''}`,
        status: isExit ? 'info' : 'success',
        duration: 3500,
        isClosable: true,
      });

      setScanCode('');
      fetchStats(currentEventId);
    } catch (err) {
      const isDuplicate = err.response?.status === 409;
      const msg = err.response?.data?.message || 'Invalid or unrecognized ticket!';
      const errData = err.response?.data?.data || null;

      if (isDuplicate) {
        playBeep('duplicate');
      } else {
        playBeep('error');
      }

      setLastResult({
        status: isDuplicate ? 'duplicate' : 'invalid',
        message: msg,
        data: errData,
        timestamp: new Date().toLocaleTimeString(),
      });

      toast({
        title: isDuplicate ? 'Warning: Already Checked In!' : 'Check-In Issue!',
        description: msg,
        status: isDuplicate ? 'warning' : 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsScanning(false);
      isScanningRef.current = false;
    }
  };

  // Manual Scan Input Submit
  const handleManualSubmit = (e) => {
    e?.preventDefault();
    processTicketCode(scanCode);
  };

  // Search Tickets manually
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      const activeEvtId = selectedEventIdRef.current || selectedEventId;
      if (!activeEvtId) {
        toast({
          title: 'Please select an event first',
          status: 'warning',
          duration: 3000,
        });
        return;
      }
      const res = await axios.get(
        `${API_BASE}/check-in/events/${activeEvtId}/tickets?q=${encodeURIComponent(searchQuery)}`,
        authHeaders
      );
      setSearchResults(res.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  // Toggle Check-in status manually
  const handleToggle = async (ticketId) => {
    try {
      await axios.post(`${API_BASE}/check-in/tickets/${ticketId}/toggle`, {}, authHeaders);
      setSearchResults((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, is_checked_in: !t.is_checked_in } : t))
      );
      fetchStats();
      playBeep('success');
      toast({
        title: 'Check-in status updated.',
        status: 'info',
        duration: 2500,
      });
    } catch (e) {
      toast({
        title: 'Failed to update check-in status',
        status: 'error',
      });
    }
  };

  // ==========================================
  // DEDICATED MOBILE SCANNER VIEW (For Gate Keepers)
  // ==========================================
  if (isGateKeeper) {
    return (
      <Box minH="100vh" bg={gkBg} color={gkTextColor} pb="80px">
        {/* Top Sticky App Bar */}
        <Box
          position="sticky"
          top="0"
          zIndex="100"
          bg={gkHeaderBg}
          borderBottom="1px solid"
          borderColor={gkBorderColor}
          px="16px"
          py="12px"
        >
          <Flex justify="space-between" align="center" mb="8px">
            <Flex align="center" gap="8px">
              <Box bg="brand.500" p="6px" borderRadius="md">
                <Icon as={MdQrCodeScanner} w="20px" h="20px" color="white" />
              </Box>
              <Box>
                <Text fontSize="md" fontWeight="bold" lineHeight="1.2">
                  GateScan Terminal
                </Text>
                <Text fontSize="xs" color={gkMutedText}>
                  {user?.username || 'Gate Keeper'}
                </Text>
              </Box>
            </Flex>

            <Flex align="center" gap="8px">
              <IconButton
                size="xs"
                variant="ghost"
                icon={<Icon as={colorMode === 'light' ? MdDarkMode : MdLightMode} />}
                onClick={toggleColorMode}
                aria-label="Toggle Theme"
              />
              {forceGatekeeperMode && (
                <Button
                  size="xs"
                  colorScheme="brand"
                  variant="solid"
                  onClick={() => setForceGatekeeperMode(false)}
                >
                  Back to Dashboard
                </Button>
              )}
              {!forceGatekeeperMode && (
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<Icon as={MdLogout} />}
                  onClick={() => signOut()}
                >
                  Sign Out
                </Button>
              )}
            </Flex>
          </Flex>

          {/* Quick Event & Gate Selectors */}
          <SimpleGrid columns={2} spacing="8px">
            <Select
              size="sm"
              bg={gkInputBg}
              borderColor={gkInputBorder}
              color={gkTextColor}
              value={selectedEventId || ''}
              onChange={(e) => handleEventChange(e.target.value)}
            >
              <option value="" style={{ background: colorMode === 'dark' ? '#1f2937' : 'white', color: colorMode === 'dark' ? 'white' : 'black' }}>
                All Events (Auto-Detect)
              </option>
              {events.map((evt) => (
                <option key={evt.id} value={evt.id} style={{ background: colorMode === 'dark' ? '#1f2937' : 'white', color: colorMode === 'dark' ? 'white' : 'black' }}>
                  {evt.title}
                </option>
              ))}
            </Select>

            <Select
              size="sm"
              bg={gkInputBg}
              borderColor={gkInputBorder}
              color={gkTextColor}
              value={gateName}
              onChange={(e) => updateGateName(e.target.value)}
            >
              {venueGates.map((g) => (
                <option key={g} value={g} style={{ background: colorMode === 'dark' ? '#1f2937' : 'white', color: colorMode === 'dark' ? 'white' : 'black' }}>
                  {g}
                </option>
              ))}
            </Select>
          </SimpleGrid>

          {/* Direction Toggle (Entry vs Exit / Check-out) */}
          <Flex gap="8px" mt="8px">
            <Button
              size="xs"
              colorScheme={scanDirection === 'in' ? 'green' : 'gray'}
              variant={scanDirection === 'in' ? 'solid' : 'outline'}
              onClick={() => updateScanDirection('in')}
              flex="1"
            >
              Entry Mode (In)
            </Button>
            <Button
              size="xs"
              colorScheme={scanDirection === 'out' ? 'purple' : 'gray'}
              variant={scanDirection === 'out' ? 'solid' : 'outline'}
              onClick={() => updateScanDirection('out')}
              flex="1"
            >
              Exit Mode (Out)
            </Button>
          </Flex>

          {/* Live Progress Pill */}
          {stats && (
            <Box mt="10px">
              <Flex justify="space-between" fontSize="xs" color="gray.300" mb="4px">
                <Text>
                  Checked In:{' '}
                  <Text as="span" fontWeight="bold" color="teal.300">
                    {stats.checked_in_count}
                  </Text>{' '}
                  / {stats.total_tickets}
                </Text>
                <Text fontWeight="bold" color="brand.300">
                  {stats.check_in_rate_percent}%
                </Text>
              </Flex>
              <Progress
                value={stats.check_in_rate_percent}
                size="xs"
                colorScheme="teal"
                borderRadius="full"
                bg="gray.800"
              />
            </Box>
          )}
        </Box>

        {/* Camera Viewport Area */}
        <Box px="16px" pt="16px">
          <Box
            position="relative"
            bg="black"
            borderRadius="2xl"
            overflow="hidden"
            border="2px solid"
            borderColor={isCameraActive ? 'brand.500' : 'gray.800'}
            boxShadow="0 10px 25px -5px rgba(0,0,0,0.7)"
            minH="320px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* HTML5 QR Camera Container */}
            <Box
              id="qr-reader-viewport"
              w="100%"
              minH="320px"
              position="relative"
              sx={{
                '& video': {
                  width: '100% !important',
                  height: '100% !important',
                  minHeight: '320px !important',
                  objectFit: 'cover !important',
                  borderRadius: '16px !important',
                  display: 'block !important',
                },
                '& #qr-shaded-region': {
                  borderColor: 'rgba(7, 86, 201, 0.6) !important',
                },
              }}
            />

            {!isCameraActive && (
              <Flex
                position="absolute"
                inset="0"
                zIndex="5"
                bg="gray.900"
                direction="column"
                align="center"
                justify="center"
                p="24px"
                textAlign="center"
              >
                <Box
                  bg="gray.800"
                  p="18px"
                  borderRadius="full"
                  mb="16px"
                  border="1px solid"
                  borderColor="gray.700"
                >
                  <Icon as={MdCameraAlt} w="36px" h="36px" color="brand.400" />
                </Box>
                <Text fontSize="lg" fontWeight="bold" mb="6px">
                  Camera is Inactive
                </Text>
                <Text fontSize="xs" color="gray.400" mb="18px" maxW="260px">
                  Click below to launch camera and start scanning tickets automatically.
                </Text>
                <Button
                  colorScheme="brand"
                  size="md"
                  leftIcon={<Icon as={MdCameraAlt} />}
                  onClick={() => startCamera()}
                  px="28px"
                  borderRadius="xl"
                  boxShadow="0 4px 14px 0 rgba(7, 86, 201, 0.4)"
                >
                  Start Camera Scanner
                </Button>
                {cameraError && (
                  <Text fontSize="xs" color="red.400" mt="12px" maxW="280px">
                    {cameraError}
                  </Text>
                )}
              </Flex>
            )}

            {/* In-Camera Floating Controls */}
            {isCameraActive && (
              <Flex
                position="absolute"
                bottom="14px"
                left="0"
                right="0"
                justify="center"
                align="center"
                gap="12px"
                zIndex="20"
              >
                <IconButton
                  size="sm"
                  borderRadius="full"
                  bg="rgba(17, 24, 39, 0.85)"
                  color="white"
                  _hover={{ bg: 'gray.800' }}
                  icon={<Icon as={MdFlipCameraAndroid} />}
                  onClick={toggleCameraFacing}
                  aria-label="Switch Camera"
                />

                {torchSupported && (
                  <IconButton
                    size="sm"
                    borderRadius="full"
                    bg={isTorchOn ? 'yellow.500' : 'rgba(17, 24, 39, 0.85)'}
                    color={isTorchOn ? 'black' : 'white'}
                    _hover={{ bg: 'gray.800' }}
                    icon={<Icon as={MdHighlight} />}
                    onClick={toggleTorch}
                    aria-label="Toggle Flashlight"
                  />
                )}

                <Button
                  size="sm"
                  colorScheme="red"
                  borderRadius="full"
                  leftIcon={<Icon as={MdStop} />}
                  onClick={stopCamera}
                  px="16px"
                >
                  Stop Camera
                </Button>
              </Flex>
            )}
          </Box>
        </Box>

        {/* Scan Result Feedback Card (Instant Flash Feedback) */}
        {lastResult && (
          <Box px="16px" mt="16px">
            <Box
              p="18px"
              borderRadius="2xl"
              bg={
                lastResult.status === 'success'
                  ? '#064e3b'
                  : lastResult.status === 'exit'
                    ? '#3b0764'
                    : lastResult.status === 'duplicate'
                      ? '#78350f'
                      : '#7f1d1d'
              }
              color="white"
              border="2px solid"
              borderColor={
                lastResult.status === 'success'
                  ? '#10b981'
                  : lastResult.status === 'exit'
                    ? '#a855f7'
                    : lastResult.status === 'duplicate'
                      ? '#f59e0b'
                      : '#ef4444'
              }
              boxShadow="0 8px 20px -4px rgba(0,0,0,0.5)"
              position="relative"
            >
              <Flex justify="space-between" align="start">
                <Flex align="center" gap="12px" flex="1" minW="0">
                  <Icon
                    as={
                      lastResult.status === 'success' || lastResult.status === 'exit'
                        ? MdCheckCircle
                        : lastResult.status === 'duplicate'
                          ? MdWarning
                          : MdError
                    }
                    w="36px"
                    h="36px"
                    flexShrink={0}
                    color={
                      lastResult.status === 'success'
                        ? '#34d399'
                        : lastResult.status === 'exit'
                          ? '#c084fc'
                          : lastResult.status === 'duplicate'
                            ? '#fbbf24'
                            : '#f87171'
                    }
                  />
                  <Box flex="1" minW="0">
                    <Text fontSize="md" fontWeight="bold" lineHeight="1.2" color="white">
                      {lastResult.status === 'success'
                        ? 'ENTRY GRANTED'
                        : lastResult.status === 'exit'
                          ? 'CHECK-OUT GRANTED'
                          : lastResult.status === 'duplicate'
                            ? 'ALREADY CHECKED IN'
                            : 'INVALID TICKET'}
                    </Text>
                    <Text fontSize="xs" color="whiteAlpha.900" mt="4px" wordBreak="break-word" overflowWrap="anywhere">
                      {formatScanMessage(lastResult.message)}
                    </Text>
                  </Box>
                </Flex>
                <IconButton
                  size="xs"
                  variant="ghost"
                  color="whiteAlpha.800"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  icon={<Icon as={MdClose} />}
                  onClick={() => setLastResult(null)}
                  aria-label="Dismiss"
                  ml="8px"
                />
              </Flex>

              {lastResult.data && (
                <Box
                  mt="12px"
                  pt="10px"
                  borderTop="1px solid"
                  borderColor="whiteAlpha.200"
                  fontSize="sm"
                  color="white"
                >
                  <Flex justify="space-between" mb="4px">
                    <Text color="whiteAlpha.700">Attendee:</Text>
                    <Text fontWeight="bold">
                      {lastResult.data.attendee_name || 'General Admission'}
                    </Text>
                  </Flex>
                  {lastResult.data.seat_guid && (
                    <Flex justify="space-between" mb="4px">
                      <Text color="whiteAlpha.700">Seat:</Text>
                      <Text fontWeight="bold" color="teal.200">
                        {lastResult.data.seat_guid}
                      </Text>
                    </Flex>
                  )}
                  {lastResult.data.ticket_tier && (
                    <Flex justify="space-between" mb="4px">
                      <Text color="whiteAlpha.700">Tier:</Text>
                      <Text fontWeight="bold">{lastResult.data.ticket_tier}</Text>
                    </Flex>
                  )}
                  {lastResult.data.first_scanned_at && (
                    <Flex justify="space-between" mb="4px">
                      <Text color="yellow.200">First Scanned:</Text>
                      <Text fontWeight="bold" color="yellow.200">
                        {new Date(lastResult.data.first_scanned_at).toLocaleTimeString()} (
                        {lastResult.data.gate_name || 'Gate'})
                      </Text>
                    </Flex>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}


        {/* Quick Action Buttons */}
        <Box px="16px" mt="16px">
          <SimpleGrid columns={2} spacing="10px">
            <Button
              size="sm"
              bg={gkInputBg}
              color={gkTextColor}
              _hover={{ bg: gkInputBorder }}
              leftIcon={<Icon as={MdKeyboard} />}
              onClick={() => setShowManualInput(!showManualInput)}
            >
              {showManualInput ? 'Hide Manual' : 'Type Barcode'}
            </Button>

            <Button
              size="sm"
              bg={gkInputBg}
              color={gkTextColor}
              _hover={{ bg: gkInputBorder }}
              leftIcon={<Icon as={MdHistory} />}
              onClick={() => setShowRecentDrawer(!showRecentDrawer)}
            >
              Recent Scans ({recentLogs.length})
            </Button>
          </SimpleGrid>
        </Box>

        {/* Manual Barcode Input Collapse */}
        <Collapse in={showManualInput} animateOpacity>
          <Box px="16px" mt="12px">
            <Box bg={gkHeaderBg} p="14px" borderRadius="xl" border="1px solid" borderColor={gkBorderColor}>
              <Text fontSize="xs" fontWeight="bold" color={gkMutedText} mb="8px">
                MANUAL BARCODE / TICKET ID ENTRY
              </Text>
              <form onSubmit={handleManualSubmit}>
                <InputGroup size="md">
                  <Input
                    placeholder="Enter code (e.g. TKT-2026-XXXX)"
                    value={scanCode}
                    onChange={(e) => setScanCode(e.target.value)}
                    bg={gkInputBg}
                    borderColor={gkInputBorder}
                    color={gkTextColor}
                    fontSize="sm"
                  />
                  <InputRightElement width="4.5rem">
                    <Button
                      h="1.75rem"
                      size="sm"
                      colorScheme="brand"
                      type="submit"
                      isLoading={isScanning}
                    >
                      Verify
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </form>
            </Box>
          </Box>
        </Collapse>

        {/* Recent Scans Drawer / Collapse */}
        <Collapse in={showRecentDrawer} animateOpacity>
          <Box px="16px" mt="12px">
            <Box bg={gkHeaderBg} p="14px" borderRadius="xl" border="1px solid" borderColor={gkBorderColor}>
              <Flex justify="space-between" align="center" mb="10px">
                <Text fontSize="xs" fontWeight="bold" color={gkMutedText}>
                  LATEST GATE ACTIVITY ({filteredRecentLogs.length})
                </Text>
                <IconButton
                  size="xs"
                  variant="ghost"
                  color={gkMutedText}
                  icon={<Icon as={MdRefresh} />}
                  onClick={() => fetchStats()}
                  aria-label="Refresh"
                />
              </Flex>

              {/* Mobile Feed Quick Filters */}
              <Flex gap="6px" mb="10px" wrap="wrap">
                <Input
                  size="xs"
                  placeholder="Filter seat / name..."
                  value={feedSeatFilter}
                  onChange={(e) => setFeedSeatFilter(e.target.value)}
                  bg={gkInputBg}
                  borderColor={gkInputBorder}
                  color={gkTextColor}
                  flex="1"
                  minW="110px"
                  borderRadius="md"
                />
                <Select
                  size="xs"
                  value={feedStatusFilter}
                  onChange={(e) => setFeedStatusFilter(e.target.value)}
                  bg={gkInputBg}
                  borderColor={gkInputBorder}
                  color={gkTextColor}
                  w="95px"
                  borderRadius="md"
                >
                  <option value="all">Status</option>
                  <option value="valid">Valid</option>
                  <option value="exit">Exit</option>
                  <option value="duplicate">Duplicate</option>
                  <option value="invalid">Invalid</option>
                </Select>
                {venueGates.length > 1 && (
                  <Select
                    size="xs"
                    value={feedGateFilter}
                    onChange={(e) => setFeedGateFilter(e.target.value)}
                    bg={gkInputBg}
                    borderColor={gkInputBorder}
                    color={gkTextColor}
                    w="95px"
                    borderRadius="md"
                  >
                    <option value="all">Gates</option>
                    {venueGates.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </Select>
                )}
              </Flex>

              {filteredRecentLogs.length === 0 ? (
                <Text fontSize="xs" color="gray.500" py="8px" textAlign="center">
                  No scan logs matching filter.
                </Text>
              ) : (
                <Flex direction="column" gap="8px" maxH="320px" overflowY="auto" pr="4px">
                  {filteredRecentLogs.map((log) => (
                    <Flex
                      key={log.id}
                      justify="space-between"
                      align="center"
                      p="8px"
                      bg={gkInputBg}
                      borderRadius="lg"
                      fontSize="xs"
                    >
                      <Box flex="1" minW="0" pr="8px">
                        <Text fontWeight="bold" color={gkTextColor} noOfLines={1}>
                          {log.attendee_name || 'Attendee'}
                        </Text>
                        <Text color={gkMutedText} fontSize="10px">
                          {log.seat_guid || 'General'} • {log.gate_name} •{' '}
                          {new Date(log.created_at).toLocaleTimeString()}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={
                          log.result_code === 'VALID' || log.status === 'success'
                            ? 'green'
                            : log.result_code === 'EXIT_GRANTED' || log.status === 'exit'
                              ? 'purple'
                              : log.result_code === 'DUPLICATE' || log.status === 'duplicate'
                                ? 'yellow'
                                : 'red'
                        }
                        fontSize="10px"
                      >
                        {log.direction === 'out' ? 'EXIT' : log.result_code || log.status?.toUpperCase() || 'VALID'}
                      </Badge>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  }

  // ==========================================
  // DESKTOP ADMIN DASHBOARD VIEW (For Admin Users)
  // ==========================================
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px="25px" pb="50px">
      {/* Header Bar */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ md: 'center' }}
        mb="25px"
        gap="15px"
      >
        <Box>
          <Flex align="center" gap="10px">
            <Icon as={MdQrCodeScanner} w="28px" h="28px" color="brand.500" />
            <Text color={textColor} fontSize="2xl" fontWeight="bold">
              Gate Check-In & Ticket Inspector
            </Text>
          </Flex>
          <Text color="gray.500" fontSize="sm">
            Live barcode and QR scanner for venue staff and admission gates.
          </Text>
        </Box>

        <Flex gap="12px" align="center" flexWrap="wrap">
          <Select
            maxW="260px"
            value={selectedEventId || ''}
            onChange={(e) => handleEventChange(e.target.value)}
            bg={cardBg}
            borderColor={borderColor}
          >
            <option value="">All Events (Auto-Detect)</option>
            {events.map((evt) => (
              <option key={evt.id} value={evt.id}>
                {evt.title}
              </option>
            ))}
          </Select>

          <Button
            colorScheme="brand"
            variant="solid"
            leftIcon={<Icon as={MdQrCodeScanner} />}
            onClick={() => setForceGatekeeperMode(true)}
          >
            Gatekeeper Scan Mode
          </Button>

          <Button
            colorScheme="brand"
            variant={isCameraActive ? 'solid' : 'outline'}
            leftIcon={<Icon as={isCameraActive ? MdStop : MdCameraAlt} />}
            onClick={() => (isCameraActive ? stopCamera() : startCamera())}
          >
            {isCameraActive ? 'Close Camera' : 'Open Camera'}
          </Button>

          <Button
            leftIcon={<Icon as={MdRefresh} />}
            variant="outline"
            onClick={fetchStats}
            isLoading={!stats}
          >
            Refresh
          </Button>
        </Flex>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="20px" mb="25px">
        <Card p="20px" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text color="gray.500" fontSize="sm" fontWeight="500">
                Total Issued Tickets
              </Text>
              <Text color={textColor} fontSize="2xl" fontWeight="bold">
                {stats?.total_tickets || 0}
              </Text>
            </Box>
            <Icon as={MdPeople} w="32px" h="32px" color="brand.500" />
          </Flex>
        </Card>

        <Card p="20px" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text color="gray.500" fontSize="sm" fontWeight="500">
                Checked-In Attendees
              </Text>
              <Text color="green.500" fontSize="2xl" fontWeight="bold">
                {stats?.checked_in_count || 0}
              </Text>
            </Box>
            <Icon as={MdCheckCircle} w="32px" h="32px" color="green.500" />
          </Flex>
        </Card>

        <Card p="20px" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text color="gray.500" fontSize="sm" fontWeight="500">
                Remaining to Arrive
              </Text>
              <Text color="orange.500" fontSize="2xl" fontWeight="bold">
                {stats?.pending_count || 0}
              </Text>
            </Box>
            <Icon as={MdWarning} w="32px" h="32px" color="orange.500" />
          </Flex>
        </Card>

        <Card p="20px" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <Box>
            <Flex justify="space-between" align="center" mb="8px">
              <Text color="gray.500" fontSize="sm" fontWeight="500">
                Attendance Rate
              </Text>
              <Text color="brand.500" fontWeight="bold">
                {stats?.check_in_rate_percent || 0}%
              </Text>
            </Flex>
            <Progress
              value={stats?.check_in_rate_percent || 0}
              size="sm"
              colorScheme="brand"
              borderRadius="full"
            />
          </Box>
        </Card>
      </SimpleGrid>

      {/* Main Scanner Container Grid */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing="20px" mb="25px">
        {/* Scanner Terminal */}
        <Card p="24px" bg={cardBg} border="1px solid" borderColor={borderColor}>
          <Text fontSize="lg" fontWeight="bold" color={textColor} mb="15px">
            Live Scanner Terminal
          </Text>

          {/* Camera Viewport Area */}
          <Box
            position="relative"
            bg="black"
            borderRadius="xl"
            overflow="hidden"
            border="2px solid"
            borderColor={isCameraActive ? 'brand.500' : 'gray.800'}
            mb="18px"
            minH="240px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              id="qr-reader-viewport"
              w="100%"
              minH="260px"
              position="relative"
              sx={{
                '& video': {
                  width: '100% !important',
                  height: '100% !important',
                  minHeight: '260px !important',
                  objectFit: 'cover !important',
                  borderRadius: '12px !important',
                  display: 'block !important',
                },
                '& #qr-shaded-region': {
                  borderColor: 'rgba(7, 86, 201, 0.6) !important',
                },
              }}
            />

            {!isCameraActive && (
              <Flex
                position="absolute"
                inset="0"
                zIndex="5"
                bg="gray.900"
                direction="column"
                align="center"
                justify="center"
                p="20px"
                textAlign="center"
              >
                <Box bg="gray.800" p="14px" borderRadius="full" mb="12px" border="1px solid" borderColor="gray.700">
                  <Icon as={MdCameraAlt} w="30px" h="30px" color="brand.400" />
                </Box>
                <Text fontSize="md" fontWeight="bold" color="white" mb="4px">
                  Camera is Inactive
                </Text>
                <Text fontSize="xs" color="gray.400" mb="14px" maxW="240px">
                  Click below to launch camera and start scanning tickets automatically.
                </Text>
                <Button
                  colorScheme="brand"
                  size="sm"
                  leftIcon={<Icon as={MdCameraAlt} />}
                  onClick={startCamera}
                  borderRadius="lg"
                >
                  Start Camera Scanner
                </Button>
                {cameraError && (
                  <Text fontSize="xs" color="red.400" mt="10px">
                    {cameraError}
                  </Text>
                )}
              </Flex>
            )}

            {isCameraActive && (
              <Flex
                position="absolute"
                bottom="10px"
                left="0"
                right="0"
                justify="center"
                gap="10px"
                zIndex="20"
              >
                <IconButton
                  size="xs"
                  borderRadius="full"
                  bg="rgba(17, 24, 39, 0.85)"
                  color="white"
                  icon={<Icon as={MdFlipCameraAndroid} />}
                  onClick={toggleCameraFacing}
                  aria-label="Switch Camera"
                />
                {torchSupported && (
                  <IconButton
                    size="xs"
                    borderRadius="full"
                    bg={isTorchOn ? 'yellow.500' : 'rgba(17, 24, 39, 0.85)'}
                    color={isTorchOn ? 'black' : 'white'}
                    icon={<Icon as={MdHighlight} />}
                    onClick={toggleTorch}
                    aria-label="Flashlight"
                  />
                )}
                <Button size="xs" colorScheme="red" onClick={stopCamera}>
                  Stop
                </Button>
              </Flex>
            )}
          </Box>

          <form onSubmit={handleManualSubmit}>
            <Box mb="15px">
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb="6px">
                CHECK-IN DIRECTION
              </Text>
              <Flex gap={3}>
                <Button
                  flex="1"
                  size="sm"
                  colorScheme={scanDirection === 'in' ? 'green' : 'gray'}
                  variant={scanDirection === 'in' ? 'solid' : 'outline'}
                  onClick={() => updateScanDirection('in')}
                >
                  Entry Mode (In)
                </Button>
                <Button
                  flex="1"
                  size="sm"
                  colorScheme={scanDirection === 'out' ? 'purple' : 'gray'}
                  variant={scanDirection === 'out' ? 'solid' : 'outline'}
                  onClick={() => updateScanDirection('out')}
                >
                  Check-Out Mode (Out)
                </Button>
              </Flex>
            </Box>

            <Box mb="15px">
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb="6px">
                GATE ENTRANCE
              </Text>
              <Select
                value={gateName}
                onChange={(e) => updateGateName(e.target.value)}
                bg={cardBg}
                borderColor={borderColor}
              >
                {venueGates.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Select>
            </Box>

            <Box mb="15px">
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb="6px">
                BARCODE / QR HASH (SCANNER READY)
              </Text>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon as={MdQrCodeScanner} color="brand.500" />
                </InputLeftElement>
                <Input
                  autoFocus
                  placeholder="Scan with gun or type barcode..."
                  value={scanCode}
                  onChange={(e) => setScanCode(e.target.value)}
                  borderColor={borderColor}
                />
              </InputGroup>
            </Box>

            <Button
              w="100%"
              colorScheme="brand"
              size="lg"
              type="submit"
              isLoading={isScanning}
              leftIcon={<Icon as={MdQrCodeScanner} />}
            >
              Verify Ticket
            </Button>
          </form>

          {/* Last Result Card */}
          {lastResult && (
            <Box
              mt="20px"
              p="16px"
              borderRadius="xl"
              bg={
                lastResult.status === 'success'
                  ? 'green.50'
                  : lastResult.status === 'exit'
                    ? 'purple.50'
                    : lastResult.status === 'duplicate'
                      ? 'yellow.50'
                      : 'red.50'
              }
              border="1px solid"
              borderColor={
                lastResult.status === 'success'
                  ? 'green.300'
                  : lastResult.status === 'exit'
                    ? 'purple.300'
                    : lastResult.status === 'duplicate'
                      ? 'yellow.300'
                      : 'red.300'
              }
            >
              <Flex align="center" gap="10px" mb="6px">
                <Icon
                  as={
                    lastResult.status === 'success' || lastResult.status === 'exit'
                      ? MdCheckCircle
                      : lastResult.status === 'duplicate'
                        ? MdWarning
                        : MdError
                  }
                  color={
                    lastResult.status === 'success'
                      ? 'green.500'
                      : lastResult.status === 'exit'
                        ? 'purple.600'
                        : lastResult.status === 'duplicate'
                          ? 'yellow.600'
                          : 'red.500'
                  }
                  w="24px"
                  h="24px"
                />
                <Text
                  fontWeight="bold"
                  color={
                    lastResult.status === 'success'
                      ? 'green.800'
                      : lastResult.status === 'exit'
                        ? 'purple.800'
                        : lastResult.status === 'duplicate'
                          ? 'yellow.800'
                          : 'red.800'
                  }
                >
                  {lastResult.status === 'success'
                    ? 'Entry Granted'
                    : lastResult.status === 'exit'
                      ? 'Check-Out Registered'
                      : lastResult.status === 'duplicate'
                        ? 'Already Scanned!'
                        : 'Access Denied'}
                </Text>
              </Flex>
              <Text fontSize="xs" color="gray.600" wordBreak="break-word" overflowWrap="anywhere">
                {formatScanMessage(lastResult.message)}
              </Text>
              {lastResult.data && (
                <Box mt="8px" fontSize="xs" color="gray.700">
                  <Text>
                    <strong>Attendee:</strong> {lastResult.data.attendee_name || 'N/A'}
                  </Text>
                  <Text>
                    <strong>Seat:</strong> {lastResult.data.seat_guid || 'General'}
                  </Text>
                  <Text>
                    <strong>Category:</strong> {lastResult.data.ticket_tier || 'Standard'}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </Card>

        {/* Live Gate Logs Table */}
        <Card
          gridColumn={{ lg: 'span 2' }}
          p="20px"
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          display="flex"
          flexDirection="column"
          h="100%"
        >
          <Flex justify="space-between" align="center" mb="12px" wrap="wrap" gap="10px">
            <Flex align="center" gap="10px">
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Real-Time Gate Audit Feed
              </Text>
              <Badge colorScheme="brand" px="3" py="1" borderRadius="full">
                {filteredRecentLogs.length} Records
              </Badge>
            </Flex>
            <IconButton
              size="xs"
              variant="outline"
              icon={<Icon as={MdRefresh} />}
              onClick={() => fetchStats()}
              aria-label="Refresh Audit Feed"
            />
          </Flex>

          {/* Audit Filters Bar */}
          <Flex gap="8px" mb="12px" wrap="wrap" align="center">
            <InputGroup size="sm" maxW={{ base: '100%', md: '180px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Seat, name, code..."
                value={feedSeatFilter}
                onChange={(e) => setFeedSeatFilter(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>

            <Select
              size="sm"
              maxW="140px"
              value={feedStatusFilter}
              onChange={(e) => setFeedStatusFilter(e.target.value)}
              borderRadius="md"
            >
              <option value="all">All Statuses</option>
              <option value="valid">Valid / Success</option>
              <option value="exit">Check-Out (Exit)</option>
              <option value="duplicate">Duplicate (Warning)</option>
              <option value="invalid">Invalid / Wrong Gate</option>
            </Select>

            <Select
              size="sm"
              maxW="150px"
              value={feedGateFilter}
              onChange={(e) => setFeedGateFilter(e.target.value)}
              borderRadius="md"
            >
              <option value="all">All Gates</option>
              {venueGates.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Select>

            {events.length > 1 && (
              <Select
                size="sm"
                maxW="170px"
                value={feedEventFilter}
                onChange={(e) => setFeedEventFilter(e.target.value)}
                borderRadius="md"
              >
                <option value="">Current Event</option>
                {events.map((evt) => (
                  <option key={evt.id} value={evt.id}>{evt.title}</option>
                ))}
              </Select>
            )}

            {(feedSeatFilter || feedStatusFilter !== 'all' || feedGateFilter !== 'all' || feedEventFilter) && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => {
                  setFeedSeatFilter('');
                  setFeedStatusFilter('all');
                  setFeedGateFilter('all');
                  setFeedEventFilter('');
                }}
              >
                Clear
              </Button>
            )}
          </Flex>

          <Box flex="1" minH="340px" maxH={{ base: '420px', lg: '620px' }} overflowY="auto" overflowX="auto" pr="4px">
            <Table variant="simple" size="sm">
              <Thead position="sticky" top={0} bg={cardBg} zIndex={1}>
                <Tr>
                  <Th>Time</Th>
                  <Th>Attendee</Th>
                  <Th>Seat / Tier</Th>
                  <Th>Gate</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRecentLogs.length === 0 ? (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py="20px" color="gray.400">
                      No scan records match the current filter.
                    </Td>
                  </Tr>
                ) : (
                  filteredRecentLogs.map((log) => (
                    <Tr key={log.id}>
                      <Td fontSize="xs" color="gray.500">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </Td>
                      <Td fontWeight="bold" color={textColor}>
                        {log.attendee_name || 'General Admission'}
                      </Td>
                      <Td fontSize="xs">
                        <Badge variant="subtle" colorScheme="purple" mr="1">
                          {log.seat_tier || 'General'}
                        </Badge>
                        {log.seat_guid && log.seat_guid !== '-' && (
                          <Text as="span" color="gray.500">({log.seat_guid})</Text>
                        )}
                      </Td>
                      <Td fontSize="xs" color="gray.500">
                        {log.gate_name}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            log.result_code === 'VALID' || log.status === 'success'
                              ? 'green'
                              : log.result_code === 'EXIT_GRANTED' || log.status === 'exit'
                                ? 'purple'
                                : log.result_code === 'DUPLICATE' || log.status === 'duplicate'
                                  ? 'yellow'
                                  : 'red'
                          }
                        >
                          {log.direction === 'out' ? 'EXIT' : log.result_code || log.status?.toUpperCase() || 'VALID'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Card>
      </SimpleGrid>

      {/* Manual Search & Attendee Lookup */}
      <Card p="24px" bg={cardBg} border="1px solid" borderColor={borderColor}>
        <Text fontSize="lg" fontWeight="bold" color={textColor} mb="15px">
          Manual Attendee Search & Override
        </Text>

        <form onSubmit={handleSearch}>
          <Flex gap="10px" mb="15px">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search attendee name, order number, or ticket code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                borderColor={borderColor}
              />
            </InputGroup>
            <Button colorScheme="brand" type="submit" px="25px">
              Search
            </Button>
          </Flex>
        </form>

        {searchResults.length > 0 && (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Ticket ID</Th>
                  <Th>Attendee Name</Th>
                  <Th>Tier / Seat</Th>
                  <Th>Check-In Status</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {searchResults.map((t) => {
                  const orderStatus = (t.order?.payment_status || '').toLowerCase();
                  const ticketStatus = (t.status || t.effective_status || '').toLowerCase();
                  const isRefunded = ticketStatus === 'refunded' || orderStatus === 'refunded';
                  const isCancelled = ticketStatus === 'cancelled' || orderStatus === 'cancelled';
                  const isVoid = isRefunded || isCancelled;

                  return (
                    <Tr key={t.id} opacity={isVoid ? 0.75 : 1}>
                      <Td fontFamily="mono" fontSize="xs" fontWeight="bold">
                        <Flex align="center" gap={1}>
                          <Text as={isVoid ? 's' : undefined}>{t.ticket_code}</Text>
                          {isVoid && (
                            <Badge colorScheme="red" variant="subtle" fontSize="9px">
                              VOID
                            </Badge>
                          )}
                        </Flex>
                      </Td>
                      <Td fontWeight="bold">
                        <Text color={isVoid ? 'gray.500' : undefined}>
                          {t.attendee_name || t.order?.customer_name || 'N/A'}
                        </Text>
                      </Td>
                      <Td>
                        {t.ticket_tier?.name} • {t.seat_guid || 'General'}
                      </Td>
                      <Td>
                        {isRefunded ? (
                          <Badge colorScheme="red">Refunded</Badge>
                        ) : isCancelled ? (
                          <Badge colorScheme="red">Cancelled</Badge>
                        ) : (
                          <Badge colorScheme={t.is_checked_in ? 'green' : 'gray'}>
                            {t.is_checked_in ? 'Checked In' : 'Not Checked In'}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        {isVoid ? (
                          <Button size="xs" isDisabled colorScheme="gray">
                            Void (Inactive)
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            colorScheme={t.is_checked_in ? 'red' : 'green'}
                            onClick={() => handleToggle(t.id)}
                          >
                            {t.is_checked_in ? 'Cancel Check-in' : 'Check In Manually'}
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
}
